import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Button } from '@picsio/ui';

import localization from '../../../shared/strings';
import styles from './CardForm.module.scss';

const elementBaseOptions = {
  style: {
    base: {
      iconStyle: 'solid',
      color: '#CCCCCC',
      '::placeholder': {
        color: '#666666',
      },
      iconColor: '#ccc',
    },
    invalid: {
      color: '#CCCCCC',
      '::placeholder': { color: '#666666' },
    },

  },
};

const CardForm = ({
  email,
  cancelLabel,
  onCancel,
  onSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { cardNumber } = localization.BILLING.cardForm;
  const { expirationDate } = localization.BILLING.cardForm;
  const { securityCode } = localization.BILLING.cardForm;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    [cardNumber]: '',
    [expirationDate]: '',
    [securityCode]: '',
  });
  const [cardCompletion, setCardCompletion] = useState({
    [cardNumber]: false,
    [expirationDate]: false,
    [securityCode]: false,
  });

  const isCardComplete = cardCompletion[cardNumber] && cardCompletion[expirationDate] && cardCompletion[securityCode];

  const handleFieldError = useCallback((field, message) => {
    setFieldErrors((prev) => ({
      ...prev,
      [field]: message || '',
    }));
  }, []);

  const onElementChange = useCallback((field) => (event) => {
    if (event.error) {
      handleFieldError(field, event.error.message);
    } else if (!event.empty) {
      handleFieldError(field, '');
    }

    setCardCompletion((prev) => ({
      ...prev,
      [field]: Boolean(event.complete),
    }));
  },
  [handleFieldError]);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || !isCardComplete) return;

    setIsSubmitting(true);

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        throw new Error('Card element is not ready yet.');
      }

      const result = await stripe.createToken(cardNumberElement, { email });

      if (result.error || !result.token) {
        throw new Error(result.error?.message || localization.BILLING.textCantValidateCard);
      }

      onSuccess(result.token);
    } catch (err) {
      setFieldErrors({ [expirationDate]: err.message || localization.BILLING.textCantValidateCard });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCancelClick = useCallback(
    (event) => {
      event.preventDefault();
      if (isSubmitting) return;
      onCancel();
    },
    [isSubmitting, onCancel],
  );

  return (
    <form className={styles.validateCardForm} onSubmit={onSubmit}>
      <div className={styles.validateCardFormFieldWrapper}>
        <label
          className={styles.validateCardFormLabel}
          htmlFor={cardNumber}
        >
          {cardNumber}
        </label>
        <div id={cardNumber}>
          <CardNumberElement
            options={{
              ...elementBaseOptions,
              showIcon: true,
              disableLink: true,
              placeholder: localization.BILLING.cardForm.cardNumberPlaceholder,
            }}
            className={cn(
              styles.validateCardFormField,
              { [styles.validateCardFormFieldError]: Boolean(fieldErrors[cardNumber]) },
            )}
            onChange={(e) => onElementChange(cardNumber)(e)}
          />
          <If condition={Boolean(fieldErrors[cardNumber])}>
            <span className={styles.validateCardFormError}>
              {fieldErrors[cardNumber]}
            </span>
          </If>
        </div>
      </div>

      <div className={styles.validateCardFormFieldRow}>
        <div className={styles.validateCardFormFieldWrapper}>
          <label className={styles.validateCardFormLabel} htmlFor={expirationDate}>
            {expirationDate}
          </label>
          <div id={expirationDate}>
            <CardExpiryElement
              className={cn(
                styles.validateCardFormField,
                { [styles.validateCardFormFieldError]: Boolean(fieldErrors[expirationDate]) },
              )}
              options={{
                ...elementBaseOptions,
                placeholder: localization.BILLING.cardForm.expirationDatePlaceholder,
              }}
              onChange={onElementChange(expirationDate)}
            />
            <If condition={Boolean(fieldErrors[expirationDate])}>
              <span className={styles.validateCardFormError}>
                {fieldErrors[expirationDate]}
              </span>
            </If>
          </div>
        </div>
        <div className={styles.validateCardFormFieldWrapper}>
          <label className={styles.validateCardFormLabel} htmlFor={securityCode}>
            {securityCode}
          </label>
          <div id={securityCode}>
            <CardCvcElement
              className={cn(
                styles.validateCardFormField,
                { [styles.validateCardFormFieldError]: Boolean(fieldErrors[securityCode]) },
              )}
              options={{
                ...elementBaseOptions,
                placeholder: localization.BILLING.cardForm.securityCodePlaceholder,
              }}
              onChange={onElementChange(securityCode)}
            />
            <If condition={Boolean(fieldErrors[securityCode])}>
              <span className={styles.validateCardFormError}>
                {fieldErrors[securityCode]}
              </span>
            </If>
          </div>
        </div>
      </div>
      <div className={styles.validateCardFormActions}>
        <Button
          type="button"
          variant="contained"
          color="secondary"
          size="md"
          onClick={onCancelClick}
          disabled={isSubmitting}
          className={styles.validateCardFormCancelBtn}
        >
          {cancelLabel || localization.DIALOGS.btnCancel}
        </Button>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          size="md"
          className={styles.validateCardFormSubmitBtn}
          disabled={!stripe || isSubmitting || !isCardComplete}
        >
          {localization.BILLING.textValidate}
        </Button>
      </div>
    </form>
  );
};

CardForm.propTypes = {
  email: PropTypes.string.isRequired,
  cancelLabel: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

CardForm.defaultProps = {
  cancelLabel: null,
};

export default CardForm;
